// handles fetching and stores the fetched assets
import { FC, useEffect } from 'react';

import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import { AssetsQueryContext, AssetsQueryContextState } from './AssetsQueryContext';
import {
  serializationOptionsForNumberValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { FOLDER_ID_SEARCH_PARAM } from '../constants';
import { useAssetsFetching } from '../hooks/useAssetsFetching';
import { useRootAssetFolderId } from '../hooks/useRootAssetFolderId';
import { SortBy } from '../types';

type Props = {
  append?: boolean;
  isPanel?: boolean;
};

export const AssetsQueryContextProvider: FC<React.PropsWithChildren<Props>> = ({
  append = false,
  isPanel = false,
  children,
}) => {
  const rootFolderId = useRootAssetFolderId();
  const { sortBy, sortDirection } = useSortingContext();
  const { currentPage, pageSize } = usePaginationContext();

  const [folderId, setFolderId] = useSearchParamState({
    searchParamKey: FOLDER_ID_SEARCH_PARAM,
    stateValueWhenSearchParamIsUnset: rootFolderId,
    ...serializationOptionsForNumberValue,
  });

  useEffect(() => {
    return () => {
      if (isPanel) {
        setFolderId(rootFolderId);
      }
    };
  }, []);

  const {
    assets,
    folders,
    assetCount,
    assetCountTotal,
    assetPage,
    currentFolder,
    isLoading,
    hasError,
    refetch,
  } = useAssetsFetching({
    isPanel,
    append,
    sortBy: SortBy[sortBy as keyof typeof SortBy],
    sortDirection,
    currentPage,
    pageSize,
    folderId,
  });

  const context: AssetsQueryContextState = {
    rootFolderId,
    folderId,
    assetCount,
    assetCountTotal,
    assetPage,
    assetPageSize: pageSize,
    assets,
    folders,
    currentFolder,
    isLoading,
    hasError,
    setFolderId,
    refetch,
    isPanel,
  };

  return <AssetsQueryContext.Provider value={context}>{children}</AssetsQueryContext.Provider>;
};
